import { useCallback, useState } from 'react';

import { LanguageKeys } from 'src/common/constants/languages';
import {
  addPageBlock,
  getBodyBlocks,
  IPageDocument,
  moveBlockOrder,
  movePageBlock,
  normalizePageDocument,
  removePageBlock,
  updatePageBlock,
} from 'src/helpers/pageDocumentUtils';
import { IPageBlock, PageBlockType } from 'src/interfaces/PageBlock';

export default function useSocialShareBlocks(
  initial: IPageDocument,
  onDocumentChange?: (doc: IPageDocument) => void,
) {
  const [document, setDocument] = useState<IPageDocument>(() => normalizePageDocument(initial));
  const [selectedEditorLang, setSelectedEditorLang] = useState<LanguageKeys>(LanguageKeys.en);

  const applyDocument = useCallback(
    (updater: (prev: IPageDocument) => IPageDocument) => {
      setDocument(prev => {
        const next = updater(prev);
        onDocumentChange?.(next);
        return next;
      });
    },
    [onDocumentChange],
  );

  const setComposedDocument = useCallback(
    (doc: IPageDocument) => {
      const next = normalizePageDocument(doc);
      setDocument(next);
      onDocumentChange?.(next);
    },
    [onDocumentChange],
  );

  const onAddAt = useCallback(
    (
      zone: 'hero' | 'body',
      type: PageBlockType,
      columnCount?: 2 | 3 | 4,
      insertIndex?: number,
    ) => {
      applyDocument(prev =>
        addPageBlock(
          prev,
          { kind: 'zone', zone, insertIndex },
          type,
          columnCount ? { columnCount } : undefined,
        ),
      );
    },
    [applyDocument],
  );

  const onUpdate = useCallback(
    (id: string, patch: Partial<IPageBlock>) => {
      applyDocument(prev => updatePageBlock(prev, id, patch));
    },
    [applyDocument],
  );

  const onRemove = useCallback(
    (id: string) => {
      applyDocument(prev => removePageBlock(prev, id));
    },
    [applyDocument],
  );

  const onMoveBlock = useCallback(
    (id: string, direction: 'up' | 'down') => {
      applyDocument(prev => moveBlockOrder(prev, id, direction));
    },
    [applyDocument],
  );

  const onMoveByDrag = useCallback(
    (activeId: string, overId: string) => {
      applyDocument(prev => movePageBlock(prev, activeId, overId));
    },
    [applyDocument],
  );

  const blocks = getBodyBlocks(document);

  return {
    document,
    blocks,
    selectedEditorLang,
    setSelectedEditorLang,
    setComposedDocument,
    onAddAt,
    onUpdate,
    onRemove,
    onMoveBlock,
    onMoveByDrag,
  };
}
